import { errorResponseSchema, type ErrorCode } from '@loctopus/shared';

/**
 * Thrown for every non-2xx HTTP response. Always carries the real HTTP status and, when the
 * server followed `errorResponseSchema` (packages/shared), the machine-readable `code` and a
 * human message. Per the project's "errors must say what happened" rule, the message never
 * collapses to a bare "failed" - even the unknown-shape fallback below states the URL, status
 * and raw body so a caller can see exactly what the server sent back.
 */
export class LoctopusApiError extends Error {
  readonly code: ErrorCode | 'unknown_error';
  readonly httpStatus: number;
  readonly requestId?: string;
  readonly details?: Record<string, unknown>;
  readonly rawBody: string;
  readonly url: string;

  private constructor(options: {
    code: ErrorCode | 'unknown_error';
    message: string;
    httpStatus: number;
    rawBody: string;
    url: string;
    requestId?: string;
    details?: Record<string, unknown>;
  }) {
    super(options.message);
    this.name = 'LoctopusApiError';
    this.code = options.code;
    this.httpStatus = options.httpStatus;
    this.requestId = options.requestId;
    this.details = options.details;
    this.rawBody = options.rawBody;
    this.url = options.url;
  }

  static fromResponse(
    httpStatus: number,
    parsedJson: unknown,
    rawBody: string,
    url: string,
  ): LoctopusApiError {
    const parsed = errorResponseSchema.safeParse(parsedJson);
    if (parsed.success) {
      const { code, message, requestId, details } = parsed.data.error;
      return new LoctopusApiError({
        code: code as ErrorCode,
        message,
        httpStatus,
        rawBody,
        url,
        requestId,
        details,
      });
    }
    const bodyPreview = rawBody.length > 0 ? rawBody.slice(0, 500) : '(empty response body)';
    return new LoctopusApiError({
      code: 'unknown_error',
      message: `${url} responded ${httpStatus} with a body that did not match the documented error envelope (packages/shared's errorResponseSchema). Raw response: ${bodyPreview}`,
      httpStatus,
      rawBody,
      url,
    });
  }
}

/** Thrown when `fetch` itself rejects (DNS/TLS/offline/abort) - never swallowed as a generic failure. */
export class LoctopusNetworkError extends Error {
  readonly url: string;
  override readonly cause?: unknown;

  constructor(url: string, cause: unknown) {
    const causeMessage = cause instanceof Error ? cause.message : String(cause);
    super(`Could not reach ${url}: ${causeMessage}`);
    this.name = 'LoctopusNetworkError';
    this.url = url;
    this.cause = cause;
  }
}
